import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux'

import { PscPhrase } from '../components/psc/psc-phrase';
import NoAccess from '../components/psc/lib/NoAccess'
import UserSelect from '../components/psc/lib/UserSelect'
import CancelButton from '../components/psc/lib/CancelButton'
import DeleteButton from '../components/psc/lib/DeleteButton'
import SaveButton, { Submit } from '../components/psc/lib/SaveButton'
import ErrorMessage from '../components/psc/lib/ErrorMessage'
import TextArea from '../components/psc/lib/TextArea'
import PickDate from '../components/psc/lib/PickDate'
import Status from '../components/psc/lib/Status'
import GncDataTable from '../components/psc/lib/GncDataTable'
import Loading from '../components/psc/lib/Loading'

import useAuthentication from '../hooks/useAuthentication';
import useUserManagement from '../hooks/useUserManagement';
import useTableSlots from '../hooks/useTableSlots';
import useProjectContents from '../hooks/useProjectContents';

const DeliveryList = () => {
    const pscState = useSelector(state => state.pscState.value)
    const navigate = useNavigate()
    const project = pscState.project
    const params = useParams()
    const isNew = params.objID === 'new'
    const projectID = Number(params.projectID)
    const objID = Number(params.objID)

    const { currentUser } = useAuthentication();

    const {
        userHasProjectAccessLevel,
    } = useUserManagement()

    const {
        dateSlot,
        filePathSlot,
        linkSlot,
        ownerSlot
    } = useTableSlots()

    const {
        updateDeliveryList,
        deleteDeliveryList,
    } = useProjectContents()

    // Object fields
    const [updatedAt, setUpdatedAt] = useState(null)
    const [title, setTitle] = useState('')
    const [note, setNote] = useState('')
    const [deliveredTo, setDeliveredTo] = useState('')
    const [deliveredByUserID, setDeliveredByUserID] = useState(0)
    const [sentDate, setSentDate] = useState(null)
    const [sentReference, setSentReference] = useState('')
    const [deliveryListStatuses, setDeliveryListStatuses] = useState([])
    const [deliveryListStatusID, setDeliveryListStatusID] = useState(1) // Default status id
    const [originalDeliveryListStatusID, setOriginalDeliveryListStatusID] = useState(1)
    const [receiptReference, setReceiptReference] = useState('')

    // Read-only
    const [listDocuments, setListDocuments] = useState([])
    const [listIncludedDocuments, setListIncludedDocuments] = useState([])

    // Form controls
    const [error, setError] = useState([])
    const [hasAccess, setHasAccess] = useState(true)
    const [submitActive, setSubmitActive] = useState(false)
    const [submitSpinner, setSubmitSpinner] = useState(false)
    const [editDocumentsSpinner, setEditDocumentsSpinner] = useState(false)
    const [hasLoaded, setHasLoaded] = useState(isNew ? true: false)

    // Table stuff
    const table = useRef(null)
    const [tableData, setTableData] = useState([])
    const localStorageOrderKey = 'tblDeliveryListContentsOrder'
    const defaultOrder = [[ 1, 'asc' ]]
    
    const columns = [
        { data: 'final_version_file_path' },
        { data: 'reference' },
        { data: 'title' },
    ]

    const tableSlots = {
        0: filePathSlot,
        1: (data, type, row) => linkSlot(data, type, row, `/portfolio/${projectID}/${row.type}/${row.id}`),
        2: (data, type, row) => linkSlot(data, type, row, `/portfolio/${projectID}/${row.type}/${row.id}`),
    }

    const tableHead = (
        <tr>
            <th style={{width: '10px'}}>&nbsp;</th>
            <th>Reference</th>
            <th>Title</th>
        </tr>
    )

    useEffect(() => {
        let data = []
        data.push(...listDocuments.map(item => {
            return {
                type: 'documents',
                id: item.id,
                title: item.title,
                reference: item.reference,
                final_version_file_path: item.final_version_file_path
            }
        }))
        data.push(...listIncludedDocuments.map(item => {
            return {
                type: 'included_documents',
                id: item.id,
                title: item.original.title,
                reference: item.original.reference,
                final_version_file_path: item.original.final_version_file_path
            }
        }))
        setTableData(data)

    }, [listDocuments, listIncludedDocuments])

    // END of table stuff

    useEffect(() => {
        fetch('/v0/delivery_list_statuses', { credentials: 'same-origin' })
            .then(function(response) {
                return response.json()
            })
            .then(function(json) {
                setDeliveryListStatuses(json.results)
            })
        
        if (isNew) {
            return
        }

        fetchObject()
    }, [objID, isNew])

    const fieldsDisabled = useMemo(() => {
        if (originalDeliveryListStatusID === 2) { // TODO HACK - This is must be configurable at backend
            return true
        }
        if (!userHasProjectAccessLevel(currentUser, projectID, 'MEMBER')) {
            return true
        }
        return false
    }, [deliveryListStatusID])
    
    const fetchObject = () => {
        fetch('/v0/delivery_lists/' + objID, { credentials: 'same-origin' })
            .then(function(response) {
                return response.json()
            })
            .then(function(json) {
                if(json.error) {
                    if( json.error === 'ENOACCESS' ) {
                        setHasAccess(false)
                    } else {
                        alert('Error: ' + json.error)
                    }
                    return
                }

                setOriginalDeliveryListStatusID(json.result.delivery_list_status_id)
                setDeliveryListStatusID(json.result.delivery_list_status_id)
                setListIncludedDocuments()
                setTitle(json.result.title)
                setNote(json.result.note)
                setDeliveredTo(json.result.delivered_to)
                setDeliveredByUserID(json.result.delivered_by_user_id)
                setSentDate(json.result.sent_date)
                setSentReference(json.result.sent_reference)
                setReceiptReference(json.result.receipt_reference)
                setListDocuments(json.result.list_documents)
                setListIncludedDocuments(json.result.list_included_documents)
                setUpdatedAt(json.result.updated_at)
                setHasLoaded(true)
            })
    }

    const toBackend = () => {
        let data = {
            project_id: projectID,
            delivery_list_status_id: deliveryListStatusID,
            title: title,
            note: note,
            delivered_to: deliveredTo,
            delivered_by_user_id: deliveredByUserID,
            sent_date: sentDate,
            sent_reference: sentReference,
            receipt_reference: receiptReference,
            updated_at: updatedAt,
        }
        return data
    }

    const validateBeforeSubmit = () => {
        const errors = []
        if ( title === null || title === '' ) {
            errors.push('Title is required')
        }
        if( deliveryListStatusID === 2 ) { // TODO HACK - This is must be configurable at backend
            if( deliveredTo === null || deliveredTo === '' ) {
                errors.push(PscPhrase('DELIVERY_LIST_LABEL_DELIVERED_TO') + ' is required')
            }
            if( deliveredByUserID === 0 ) {
                errors.push(PscPhrase('DELIVERY_LIST_LABEL_DELIVERED_BY') + ' is required')
            }
            if( sentDate === null || sentDate === '' ) {
                errors.push(PscPhrase('DELIVERY_LIST_LABEL_SENT_DATE') + ' is required')
            }
            if( sentReference === null || sentReference === '' ) {
                errors.push(PscPhrase('DELIVERY_LIST_LABEL_SENT_REFERENCE') + ' is required')
            }
        }

        if ( errors.length > 0 ) {
            setError(errors)
            return false
        }
        return true
    }
    
    const handleSubmitError = (props) => {
        setSubmitActive(false)
        setSubmitSpinner(false)
        setEditDocumentsSpinner(false)
        setError([ props.message ])
    }

    const handleSubmit = (event) => {
        if( event ) {
            event.preventDefault()
        }

        if( submitActive || !validateBeforeSubmit() ) {
            return null
        }

        setSubmitActive(true)
        setSubmitSpinner(true)

        let submit = {
            objType: 'delivery_lists',
            objID: objID,
            data: toBackend(),
            successCallback: function(json) {
                updateDeliveryList(json.result)
            },
            navigateOverride: function(json) {
                navigate('/portfolio/'+projectID+'/delivery_lists')
            }
        }
        return submit
    }

    const handleEditDocuments = (event) => {
        if( event ) {
            event.preventDefault()
        }

        if( submitActive || !validateBeforeSubmit() ) {
            return null
        }

        setSubmitActive(true)
        setEditDocumentsSpinner(true)

        let submit = {
            objType: 'delivery_lists',
            objID: objID,
            data: toBackend(),
            successCallback: function(json) {
                updateDeliveryList(json.result)
            },
            navigateOverride: function(json) {
                navigate('/portfolio/'+projectID+'/delivery_lists/'+json.result.id+'/documents')
            }
        }
        return submit
    }

    if (!hasAccess) {
        return <NoAccess />
    }

    if (!hasLoaded) {
        return <Loading />
    }

    const requiredForReservedIndicator = (deliveryListStatusID === 1 && !fieldsDisabled) 
        ? (<label className="form-required-star">&nbsp;*</label>) 
        : '';

    const requiredForLiveIndicator = (deliveryListStatusID === 2 && !fieldsDisabled)
        ? (<label className="form-required-star">&nbsp;*</label>)
        : '';

    let deleteButton = []
    if ( !isNew && userHasProjectAccessLevel(currentUser,projectID, "MEMBER")) {
        const url = '/v0/delivery_lists/'+objID;
        deleteButton.push(
            <DeleteButton
                url={url}
                onError={handleSubmitError}
                callback={() => deleteDeliveryList(objID)}
                navigateOverride={() => navigate('/portfolio/'+projectID+'/delivery_lists')}
            />
        )
    }
    
    return (
        <div className="wrapper wrapper-content">
            <div className="row">
                <div className="col-lg-12">
                    <div className="ibox float-e-margins">
                        <div className="ibox-title">
                            <h2>Delivery: {title || '<New Delivery List>'}</h2>

                        </div>
                        <div className="ibox-content">
                            <form className="form-horizontal" onSubmit={(e) => handleSubmit(e)}>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">{PscPhrase('LABEL_TITLE')}
                                        {requiredForReservedIndicator}{requiredForLiveIndicator}
                                    </label>
                                    <div className="col-sm-8">
                                        <input className="form-control" type="text" value={title} onChange={(e) => setTitle(e.target.value)} required disabled={fieldsDisabled} />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">{PscPhrase('DELIVERY_LIST_LABEL_NOTE')}</label>
                                    <div className="col-sm-8">
                                        <TextArea className="form-control" value={note} onChange={(e) => setNote(e.target.value)} disabled={fieldsDisabled} />
                                    </div>
                                </div> 

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">{PscPhrase('DELIVERY_LIST_LABEL_DELIVERED_TO')}
                                        {requiredForLiveIndicator}
                                    </label>
                                    <div className="col-sm-8">
                                        <input className="form-control" type="text" value={deliveredTo} onChange={(e) => setDeliveredTo(e.target.value)} required={fieldsDisabled} disabled={fieldsDisabled} />
                                    </div>
                                </div>

                                <UserSelect
                                    label={PscPhrase('DELIVERY_LIST_LABEL_DELIVERED_BY')}
                                    value={deliveredByUserID}
                                    onChange={(value) => setDeliveredByUserID(value)}
                                    required={deliveryListStatusID === 2 && !fieldsDisabled}
                                    disabled={fieldsDisabled}
                                    projectId={projectID}
                                />

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">{PscPhrase('DELIVERY_LIST_LABEL_SENT_DATE')}
                                        {requiredForLiveIndicator}
                                    </label>
                                    <div className="col-sm-8">
                                        <PickDate
                                            value={sentDate}
                                            onChange={(date) => setSentDate(date)}
                                            disabled={fieldsDisabled}
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">{PscPhrase('DELIVERY_LIST_LABEL_SENT_REFERENCE')}
                                        {requiredForLiveIndicator}
                                    </label>
                                    <div className="col-sm-8">
                                        <input className="form-control" type="text" value={sentReference} onChange={(e) => setSentReference(e.target.value)} required={fieldsDisabled} disabled={fieldsDisabled} />
                                    </div>
                                </div>

                                <Status
                                    value={deliveryListStatusID}
                                    onChange={(value) => setDeliveryListStatusID(value) }
                                    statuses={deliveryListStatuses}
                                    disabled={!userHasProjectAccessLevel(currentUser, projectID, "MEMBER")}
                                />

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">{PscPhrase('DELIVERY_LIST_LABEL_RECEIPT_REFERENCE')}</label>
                                    <div className="col-sm-8">
                                        <input className="form-control" type="text" value={receiptReference} onChange={(e) => setReceiptReference(e.target.value)} disabled={fieldsDisabled} />
                                    </div>
                                </div>

                                <div className="hr-line-dashed"></div>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label"></label>
                                    <div className="col-sm-8">
                                        <h3>Document Delivery List</h3>

                                        <GncDataTable
                                            tableRef={table}
                                            data={tableData}
                                            defaultOrder={defaultOrder}
                                            localStorageOrderKey={localStorageOrderKey}
                                            columns={columns}
                                            tableSlots={tableSlots}
                                            tableHead={tableHead}
                                            tableFoot={tableHead}
                                        />
                                    </div>

                                    <div className="col-sm-8 col-sm-offset-2">
                                        {!fieldsDisabled &&
                                         <SaveButton
                                             buttonVariant='info'
                                             visible={userHasProjectAccessLevel(currentUser,projectID, "MEMBER")}
                                             spinnerActive={editDocumentsSpinner}
                                             label='Edit Document Delivery List'
                                             onClick={handleEditDocuments}
                                             onError={handleSubmitError}
                                         />
                                        }
                                    </div>
                                </div>

                                <div className="hr-line-dashed"></div>

                                <div className="form-group">
                                    <ErrorMessage
                                        error={error}
                                    />

                                    <div className="col-sm-8 col-sm-offset-2">
                                        <CancelButton
                                            navigateOverride={() => navigate('/portfolio/'+projectID+'/delivery_lists')}
                                        />

                                        <SaveButton
                                            buttonVariant='primary'
                                            visible={userHasProjectAccessLevel(currentUser,projectID, "MEMBER")}
                                            spinnerActive={submitSpinner}
                                            label='Save Changes'
                                            onClick={handleSubmit}
                                            onError={handleSubmitError}
                                        />

                                        {deleteButton}
                                    </div>
                                </div>

                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
};

export default DeliveryList
